import { useState, useEffect, useMemo } from 'react';
import { Sparkles, Trophy, Check, X, ArrowRight, RotateCcw, Star, Gem, Eye, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { crystals } from '@/data/crystalData';
import { supabase } from '@/integrations/supabase/client';
import { useGameSounds } from '@/hooks/useGameSounds';
interface IdentificationQuestion {
  crystal: typeof crystals[0];
  options: string[];
  correctAnswer: string;
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function generateIdentificationQuestions(count: number): IdentificationQuestion[] {
  const shuffledCrystals = shuffleArray(crystals);
  const selectedCrystals = shuffledCrystals.slice(0, Math.min(count, crystals.length));
  
  return selectedCrystals.map(crystal => {
    // Get 3 random wrong answers
    const wrongAnswers = shuffleArray(
      crystals.filter(c => c.id !== crystal.id)
    ).slice(0, 3).map(c => c.name);
    
    // Combine with correct answer and shuffle
    const options = shuffleArray([crystal.name, ...wrongAnswers]);
    
    return {
      crystal,
      options,
      correctAnswer: crystal.name
    };
  });
}

interface CrystalIdentificationGameProps {
  onBack?: () => void;
}

export function CrystalIdentificationGame({ onBack }: CrystalIdentificationGameProps) {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'result'>('menu');
  const [questions, setQuestions] = useState<IdentificationQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);
  const [streak, setStreak] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const { playSound, preloadSounds, soundsEnabled, toggleSounds } = useGameSounds();

  useEffect(() => {
    preloadSounds(['correct', 'incorrect', 'levelComplete', 'streak']);
  }, [preloadSounds]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setTotalAttempts(data.total_quizzes_completed || 0);
        setTotalCorrect(data.correct_answers || 0);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const startGame = (questionCount: number) => {
    const newQuestions = generateIdentificationQuestions(questionCount);
    setQuestions(newQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setXpEarned(0);
    setStreak(0);
    setGameState('playing');
  };

  const handleAnswerSelect = async (answer: string) => {
    if (selectedAnswer) return;
    
    setSelectedAnswer(answer);
    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = answer === currentQuestion.correctAnswer;

    if (isCorrect) {
      const streakBonus = streak >= 3 ? 5 : streak >= 2 ? 3 : 0;
      const pointsEarned = 10 + streakBonus;
      setScore(score + 1);
      setXpEarned(xpEarned + pointsEarned);
      setStreak(streak + 1);
      
      // Play streak sound for 3+ streak, otherwise correct sound
      if (streak >= 2) {
        playSound('streak');
      } else {
        playSound('correct');
      }
    } else {
      setStreak(0);
      playSound('incorrect');
    }

    setShowResult(true);

    // Save to database
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from('crystal_quiz_answers').insert({
          user_id: user.id,
          crystal_name: currentQuestion.crystal.name,
          question_type: 'identification',
          question: `Identify this crystal`,
          user_answer: answer,
          correct_answer: currentQuestion.correctAnswer,
          is_correct: isCorrect,
          xp_awarded: isCorrect ? 10 : 0
        });
      }
    } catch (error) {
      console.error('Error saving answer:', error);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      finishGame();
    }
  };

  const finishGame = async () => {
    setGameState('result');
    playSound('levelComplete');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingProgress } = await supabase
        .from('crystal_learning_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const today = new Date().toISOString().split('T')[0];
      const newTotalXp = (existingProgress?.xp_earned || 0) + xpEarned;

      if (existingProgress) {
        await supabase
          .from('crystal_learning_progress')
          .update({
            total_quizzes_completed: existingProgress.total_quizzes_completed + 1,
            correct_answers: existingProgress.correct_answers + score,
            xp_earned: newTotalXp,
            last_quiz_date: today
          })
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('crystal_learning_progress')
          .insert({
            user_id: user.id,
            total_quizzes_completed: 1,
            correct_answers: score,
            xp_earned: xpEarned,
            last_quiz_date: today
          });
      }

      setTotalAttempts((existingProgress?.total_quizzes_completed || 0) + 1);
      setTotalCorrect((existingProgress?.correct_answers || 0) + score);
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / (totalAttempts * 5)) * 100) : 0;

  return (
    <div className="space-y-6">
      <AnimatePresence mode="wait">
        {gameState === 'menu' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Stats Overview */}
            <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-primary/20">
                    <Eye className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Crystal Recognition</p>
                    <p className="text-xs text-muted-foreground">Learn to identify crystals by sight</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-background/50 rounded-lg">
                    <p className="text-lg font-semibold text-accent">{totalAttempts}</p>
                    <p className="text-xs text-muted-foreground">Games</p>
                  </div>
                  <div className="text-center p-2 bg-background/50 rounded-lg">
                    <p className="text-lg font-semibold text-accent">{totalCorrect}</p>
                    <p className="text-xs text-muted-foreground">Correct</p>
                  </div>
                  <div className="text-center p-2 bg-background/50 rounded-lg">
                    <p className="text-lg font-semibold text-primary">{crystals.length}</p>
                    <p className="text-xs text-muted-foreground">Crystals</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Game Start Card */}
            <Card className="bg-muted/30">
              <CardHeader className="text-center pb-2">
                <CardTitle className="flex items-center justify-center gap-2 text-lg">
                  <Gem className="h-5 w-5 text-primary" />
                  Name That Crystal!
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Look at the crystal image and choose its name
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {onBack && (
                  <Button onClick={onBack} variant="ghost" className="w-full mb-2">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Learning Hub
                  </Button>
                )}
                <Button 
                  onClick={() => startGame(5)} 
                  className="w-full"
                  size="lg"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Quick Round (5 crystals)
                </Button>
                <Button 
                  onClick={() => startGame(10)} 
                  variant="outline"
                  className="w-full"
                >
                  Full Round (10 crystals)
                </Button>
                <Button 
                  onClick={() => startGame(24)} 
                  variant="ghost"
                  className="w-full text-muted-foreground"
                >
                  Master Challenge (All 24)
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {gameState === 'playing' && currentQuestion && (
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-4"
          >
            {/* Progress Bar */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Crystal {currentQuestionIndex + 1} of {questions.length}
              </span>
              <div className="flex items-center gap-2">
                {streak >= 2 && (
                  <span className="text-orange-500 font-medium">
                    🔥 {streak} streak
                  </span>
                )}
                <span className="text-primary font-medium">+{xpEarned} XP</span>
              </div>
            </div>
            <Progress 
              value={((currentQuestionIndex + 1) / questions.length) * 100} 
              className="h-2"
            />

            {/* Crystal Image Card */}
            <Card className="bg-gradient-to-br from-muted/50 to-background overflow-hidden">
              <CardContent className="p-0">
                <div className="relative aspect-square max-h-64 mx-auto bg-gradient-to-br from-background to-muted/30 flex items-center justify-center p-8">
                  <motion.img
                    src={currentQuestion.crystal.image}
                    alt="Mystery Crystal"
                    className="max-w-full max-h-full object-contain drop-shadow-lg"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                  {!showResult && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent" />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Question */}
            <div className="text-center">
              <p className="text-lg font-medium text-foreground">
                What crystal is this?
              </p>
              <p className="text-sm text-muted-foreground">
                Choose the correct name
              </p>
            </div>

            {/* Answer Options */}
            <div className="grid grid-cols-2 gap-3">
              {currentQuestion.options.map((option, index) => {
                const isCorrect = option === currentQuestion.correctAnswer;
                const isSelected = option === selectedAnswer;
                
                let buttonVariant: 'outline' | 'default' | 'destructive' = 'outline';
                let buttonClass = 'h-auto py-4 text-sm';
                
                if (showResult) {
                  if (isCorrect) {
                    buttonClass += ' bg-accent/20 border-accent text-accent-foreground';
                  } else if (isSelected) {
                    buttonClass += ' bg-destructive/20 border-destructive text-destructive';
                  }
                }

                return (
                  <Button
                    key={index}
                    variant={buttonVariant}
                    className={buttonClass}
                    onClick={() => handleAnswerSelect(option)}
                    disabled={!!selectedAnswer}
                  >
                    <span className="flex items-center justify-center gap-2 w-full">
                      <span className="flex-1 text-center">{option}</span>
                      {showResult && isCorrect && (
                        <Check className="h-4 w-4 text-accent flex-shrink-0" />
                      )}
                      {showResult && isSelected && !isCorrect && (
                        <X className="h-4 w-4 text-destructive flex-shrink-0" />
                      )}
                    </span>
                  </Button>
                );
              })}
            </div>

            {/* Result Feedback */}
            <AnimatePresence>
              {showResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Card className={`border-2 ${
                    selectedAnswer === currentQuestion.correctAnswer 
                      ? 'bg-accent/10 border-accent/30' 
                      : 'bg-destructive/5 border-destructive/20'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {selectedAnswer === currentQuestion.correctAnswer ? (
                          <div className="p-2 rounded-full bg-accent/20">
                            <Check className="h-4 w-4 text-accent" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-full bg-destructive/20">
                            <X className="h-4 w-4 text-destructive" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-foreground">
                            {selectedAnswer === currentQuestion.correctAnswer 
                              ? `Correct! +${10 + (streak >= 3 ? 5 : streak >= 2 ? 3 : 0)} XP`
                              : `It's ${currentQuestion.correctAnswer}`}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {currentQuestion.crystal.description.split('.')[0]}.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Button onClick={nextQuestion} className="w-full mt-4">
                    {currentQuestionIndex < questions.length - 1 ? (
                      <>
                        Next Crystal
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        See Results
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {gameState === 'result' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4"
          >
            <Card className="bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 border-primary/30">
              <CardHeader className="text-center">
                <div className="mx-auto p-4 rounded-full bg-primary/20 w-fit mb-4">
                  <Trophy className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl">
                  {score === questions.length ? 'Perfect!' : 'Complete!'}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-accent">{score}/{questions.length}</p>
                    <p className="text-sm text-muted-foreground">Identified</p>
                  </Card>
                  <Card className="bg-background/50 p-4">
                    <p className="text-3xl font-bold text-primary">+{xpEarned}</p>
                    <p className="text-sm text-muted-foreground">XP Earned</p>
                  </Card>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Accuracy</p>
                  <Progress value={(score / questions.length) * 100} className="h-3 mb-2" />
                  <p className="text-lg font-semibold text-foreground">
                    {Math.round((score / questions.length) * 100)}%
                  </p>
                </div>

                {score === questions.length && (
                  <div className="flex items-center justify-center gap-2 text-accent">
                    <Star className="h-5 w-5 fill-current" />
                    <span className="font-medium">Crystal Master!</span>
                    <Star className="h-5 w-5 fill-current" />
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setGameState('menu')}
                    className="flex-1"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Menu
                  </Button>
                  <Button
                    onClick={() => startGame(5)}
                    className="flex-1"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Play Again
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
